import isEmpty from 'lodash/isEmpty'
import { PROFILE_API } from '~/configs/api'
import { useUserData } from '~/composables/account/use-user-data'
import { useLogout } from '~/composables/account/use-logout'
import { useLogin } from '~/composables/account/use-login'
import { useApi } from '~/composables/common/use-api'
import { useModal } from '~/composables/common/use-modal'
import { ResponseStatus } from '~composables/common/interface'
import type { User, UserBody } from '~/composables/account/interface'
import { toastPopup } from '~/utils/modal/toast'

interface UpdateProfileOptions {
  isDisplayNameOnly: boolean
}

const DISPLAYNAME_MAX_UPDATE_TIMES = 1

export function useUpdateProfile(options: UpdateProfileOptions = { isDisplayNameOnly: false }) {
  const { isDisplayNameOnly } = options
  const { isMobile } = useDevice()

  const { request } = useApi()
  const { closeAllModal } = useModal()
  const { store, currentUser } = useUserData()
  const { submitLogout } = useLogout()
  const { showModalLogin } = useLogin()

  const messageEmailExist = ref('')
  const messagePhoneExist = ref('')
  const messageError = ref('')
  const user = reactive<User>({
    email: currentUser.value.email as string,
    fullname: currentUser.value.fullname,
    phoneNumber: currentUser.value.phone
  })

  const isDisplaynameChangingDisabled = computed(() => {
    return currentUser.value.update_fullname_count
      && currentUser.value.update_fullname_count >= DISPLAYNAME_MAX_UPDATE_TIMES
  })

  const isDisableSubmit = computed(() => {
    return messageEmailExist.value || messageError.value || messagePhoneExist.value
      || (user.fullname === currentUser.value.fullname && currentUser.value.email && currentUser.value.phone)
  })
  function resetError(): void {
    messageEmailExist.value = ''
    messagePhoneExist.value = ''
    messageError.value = ''
  }

  const maskEmail = (emailNeedMask: string): string => {
    const num = 3
    const mask = '*'
    if (!emailNeedMask) {
      return ''
    }
    const [emailPrefix, domain] = emailNeedMask.split('@')
    const maskedFisrt = emailPrefix.substring(0, num) + emailPrefix.substring(num).replace(/./g, mask)
    const maskedPrefix = emailPrefix.length > num ? maskedFisrt : emailPrefix
    return `${maskedPrefix}@${domain}`
  }

  async function checkEmailExist(email: string, username: string): Promise<boolean> {
    try {
      const { data } = await request(PROFILE_API.CHECK_EMAIL, {
        method: 'POST',
        body: {
          email,
          username
        }
      })
      return !!data?.exists
    } catch {
      return true
    }
  }

  async function checkPhoneExist(phone: string): Promise<boolean> {
    try {
      const { data } = await request(PROFILE_API.CHECK_PHONE, {
        method: 'POST',
        body: {
          phone
        }
      })
      return !!data?.exists
    } catch {
      return true
    }
  }

  async function checkDisplayNameExist(fullname: string): Promise<boolean> {
    try {
      const { data, status } = await request(PROFILE_API.CHECK_DISPLAYNAME, { method: 'POST', body: { fullname } })
      if (status === ResponseStatus.OK) {
        return !!data.exists
      }
      return true
    } catch {
      return true
    }
  }

  async function handleSubmit(): Promise<void> {
    messageError.value = ''

    if (!currentUser.value.email && user.email) {
      if (await checkEmailExist(user.email, currentUser.value.username)) {
        messageEmailExist.value = 'Email đã tồn tại'
        return
      }
    }
    if (!currentUser.value.phone && user.phoneNumber) {
      if (await checkPhoneExist(user.phoneNumber)) {
        messagePhoneExist.value = 'Số điện thoại đã được đăng ký'
        return
      }
    }

    if (currentUser.value.fullname !== user.fullname) {
      if (await checkDisplayNameExist(user.fullname)) {
        messageError.value = 'Tên hiển thị này đã tồn tại'
        return
      }
    }
    const body: UserBody = {}
    if (user.fullname !== currentUser.value.fullname) {
      body.fullname = user.fullname
    }
    if (user?.email?.length && !currentUser.value?.email) {
      body.email = user?.email
    }
    if (user?.phoneNumber?.length && !currentUser.value?.phone) {
      body.phone = user?.phoneNumber
    }
    if (isEmpty(body)) {
      return
    }
    try {
      const response = await request(PROFILE_API.UPDATE_PROFILE, {
        method: 'PUT',
        body
      })
      if (response?.status === ResponseStatus.OK) {
        if (body.fullname) {
          store.setFullname(user.fullname)
        }
        if (body?.email) {
          store.setEmail(maskEmail(body.email))
        }
        if (body?.phone) {
          store.setPhone(body.phone)
        }
        closeAllModal()
        const successMessage = isDisplayNameOnly ? 'Cập nhập tên hiển thị thành công' : 'Cập nhập thông tin thành công'
        toastPopup({ title: successMessage, type: 'success', isMobile })

        if (isDisplayNameOnly) {
          await submitLogout(false)
          showModalLogin()
        }
      }
      if (response?.message) {
        const message = response.message
        messageError.value = message ?? 'Đã xảy ra lỗi. Vui lòng kiểm tra lại!'
      }
    } catch {
      toastPopup({ title: 'Lỗi mạng, vui lòng thử lại', type: 'error', isMobile })
    }
  }
  return {
    user,
    handleSubmit,
    isDisableSubmit,
    checkDisplayNameExist,
    checkPhoneExist,
    messageEmailExist,
    messagePhoneExist,
    resetError,
    messageError,
    isDisplaynameChangingDisabled
  }
}
